import Splide from '@splidejs/splide';
import { Intersection } from '@splidejs/splide-extension-intersection';

/**
 * スライダー
 *
 * .splide クラスを持つ要素に対してスライダーを初期化します
 *
 * 使用例:
 * document.addEventListener('DOMContentLoaded', () => {
 *   new Slider(); // デフォルトセレクターを使用
 *   // または
 *   new Slider('.custom-slider'); // カスタムセレクターを指定
 * });
 *
 * 追加機能01:
 * .splide__trackWrapper で .splide__track をラップしている場合、
 * .splide__trackWrapperの左側からbodyの右端（スクロールバー）までの距離を取得して、
 * その幅を.splide__trackWrapperの幅にします。
 * リサイズが発生した場合、再計算する。
 *
 * 追加機能02:
 * is-splide--spDestoy クラスを持つ場合、スライダーを破棄します。
 * ブレークポイントは 960px です。
 *
 * 追加機能03:
 * tabindex="-1" の変更を監視し、必要に応じて下層要素の tabindex を管理します。
 */
class Slider {
  private readonly selector: string;
  private splideInstances: Map<Element, Splide> = new Map();
  private resizeListeners: Map<Element, () => void> = new Map();
  private readonly breakpoint: number = 960;

  /**sr-only
   * コンストラクター
   * @param elementSelector - スライダー要素のセレクター
   */
  constructor(elementSelector: string = '.splide') {
    this.selector = elementSelector;
    this.init();
  }

  /**
   * 初期化
   */
  private init(): void {
    const elements = document.querySelectorAll(this.selector);
    if (!elements.length) {
      console.warn('スライダー要素が見つかりません');
      return;
    }

    elements.forEach((element) => {
      // --- スクリーンリーダー向け操作説明を自動挿入 ---
      const guide = document.createElement('div');
      guide.className = 'splide__play';
      guide.setAttribute(
        'aria-description',
        'スクリーンリーダーをご利用の場合、操作方法の案内があります。'
      );
      guide.innerHTML = `
        <div class="ist-sr-only">
          <p>
            このカルーセルをスクリーンリーダーで使用するには、まず、次の autoplay ボタンを選択して、カルーセルを停止してください。
            さらに、次に進むと、現在、画面に表示されている最初のスライドの番号と、スライドの枚数が読み上げられます。
            次に進むと、previous slide / Go to last slide ボタンがあり、これを選択すると、1つ次に移動し、そのスライドの内容を読み上げます。
            次の next slide / Go to first slide を選択した場合は、1つ前に進み。そのスライドの内容を読み上げます。
            さらにフォーカスを進めると、現在完全に表示されているスライドを読み上げます。
          </p>
        </div>
      `;

      // .splide__headerItem--02がある場合はその兄として挿入、ない場合は従来通り
      const headerItem = element.querySelector('.splide__headerItem--02');
      if (headerItem) {
        headerItem.parentNode?.insertBefore(guide, headerItem);
      } else {
        element.parentNode?.insertBefore(guide, element);
      }
      // idを取得
      let id = element.id;
      if (!id) {
        // idがない場合はidを生成
        id = `ist-splide-${Math.random().toString(36).substring(2, 15)}`;
        // idをセット
        element.id = id;
      }

      // 追加機能02: is-splide--spDestoy クラスを持つか確認
      const shouldDestroyOnBreakpoint = element.classList.contains(
        'is-splide--spDestoy'
      );

      if (shouldDestroyOnBreakpoint) {
        // 初期化するかどうかを判断
        if (window.innerWidth >= this.breakpoint) {
          this.initializeSplide(element, id);
        }

        // リサイズ時の処理を設定
        const onResize = this.handleResize.bind(this, element, id);
        window.addEventListener('resize', onResize);
        this.resizeListeners.set(element, onResize);
      } else {
        // 追加機能02が不要な場合は通常通り初期化
        this.initializeSplide(element, id);
      }

      /**
       * 追加機能01: .splide__trackWrapper の幅を調整
       */
      const trackWrapper = element.querySelector(
        '.splide__trackWrapper'
      ) as HTMLElement | null;
      if (trackWrapper) {
        const adjustTrackWrapperWidth = () => {
          // bodyのクライアント幅を取得
          const bodyWidth = document.body.clientWidth;

          // .splide__trackWrapperの左端の位置を取得（ページ全体の左からの距離）
          const trackRect = trackWrapper.getBoundingClientRect();
          const trackLeft = trackRect.left + window.scrollX;

          // bodyの右端までの距離を計算
          const distanceToRight = bodyWidth - trackLeft;

          // 計算した距離を.splide__trackWrapperの幅として設定
          trackWrapper.style.width = `${distanceToRight}px`;
        };

        /**
         * デバウンス関数
         * @param func - 実行する関数
         * @param wait - 待機時間（ミリ秒）
         * @returns デバウンスされた関数
         */
        const debounce = (func: Function, wait: number) => {
          let timeout: number | undefined;
          return () => {
            clearTimeout(timeout);
            timeout = window.setTimeout(() => {
              func();
            }, wait);
          };
        };

        // デバウンスされた関数を作成
        const debouncedAdjustTrackWrapperWidth = debounce(
          adjustTrackWrapperWidth,
          100
        );

        // 初回調整
        adjustTrackWrapperWidth();

        // リサイズ時にデバウンスされた関数を呼び出す
        window.addEventListener('resize', debouncedAdjustTrackWrapperWidth);

        // クリーンアップ用に、スライダーが破棄された際にイベントリスナーを解除
        if (shouldDestroyOnBreakpoint) {
          const splide = this.splideInstances.get(element);
          if (splide) {
            splide.on('destroy', () => {
              window.removeEventListener(
                'resize',
                debouncedAdjustTrackWrapperWidth
              );
            });
          }
        }
      }
    });
  }

  /**
   * Splideを初期化し、関連機能をセットアップします
   * @param element - スライダー要素
   * @param id - 要素のID
   */
  private initializeSplide(element: Element, id: string): void {
    if (this.splideInstances.has(element)) {
      // 既に初期化されている場合はスキップ
      return;
    }

    // HTMLのdata-splide属性からオプションを取得
    const dataSplide = element.getAttribute('data-splide');
    let options: any = {};

    if (dataSplide) {
      try {
        options = JSON.parse(dataSplide);
      } catch (error) {
        console.warn('data-splide属性のJSON解析に失敗しました:', error);
      }
    }

    // アクセシビリティオプションを追加（常に左端だけをアクティブに固定）
    //options.live = false; // 標準ライブリージョン無効
    options.focus = 0; // 左端を常にアクティブ扱い
    options.group = 1; // 常に1つのグループとして扱う
    options.keyboard = false; // デフォルトのキーボード操作を無効にする
    options.trimSpace = true; // 端の余白を切る
    options.padding = { left: 0, right: 0 }; // 左右のパディングを0に
    options.slideFocus = false; // スライド要素への自動 tabindex 付与を抑止

    // loopタイプの場合は特に注意が必要
    if (options.type === 'loop') {
      options.focus = 0; // 明示的に左端固定
      options.rewind = false; // rewindを無効にして一方向移動
    }

    // 念のため全ブレークポイントで左端固定
    options.breakpoints = {
      1920: { focus: 0 },
      1280: { focus: 0 },
      960: { focus: 0 },
      640: { focus: 0 },
    };

    // スライダーを初期化
    const slider = new Splide(`#${id}`, options);

    // マウント前にイベントをバインド
    slider.on('ready', () => {
      // マウント完了時に初期状態をチェック
      //console.debug('ready');
      // 初期状態でaria-hiddenを制御
      this.controlAriaHidden(slider);
    });

    // 追加機能03: tabindexの管理
    this.setupTabindexManagement(slider);

    // スライダーをマウント
    slider.mount({ Intersection });
    this.splideInstances.set(element, slider);

    // 移動完了時にaria-hiddenを制御し、フォーカスを移動
    slider.on('moved', () => {
      this.controlAriaHidden(slider);
      // 移動完了後にフォーカスを移動
      setTimeout(() => {
        this.focusLeftmostSlide(slider);
      }, 0);
    });

    // 初期化時のみフォーカスを設定
    slider.on('mounted', () => {
      this.controlAriaHidden(slider);
      setTimeout(() => {
        this.focusLeftmostSlide(slider);
      }, 0);
    });

    // .splide__track配下でのみ矢印キー操作を有効にする
    this.setupTrackKeyboardNavigation(slider, element);

    /**
     * 自動再生機能付きの場合
     */
    // .slide-current / .slide-length を取得
    const slideCurrent = element.querySelector('.slide-current');
    const slideLength = element.querySelector('.slide-length');

    // もし、.slide-current / .slide-length があれば、それぞれの要素にテキストをセット
    if (slideCurrent && slideLength) {
      // 初期値を設定
      this.updateSlideInfo(slider, slideCurrent, slideLength);

      // スライド切り替え時のイベントリスナーを設定
      slider.on('moved', () => {
        this.updateSlideInfo(slider, slideCurrent, slideLength);
      });

      slider.on('active', () => {
        this.updateSlideInfo(slider, slideCurrent, slideLength);
      });
    }

    /**
     * 自動再生機能なしの場合
     */
    // .ist-carouselProgress / .ist-carouselProgress__bar を取得
    const carouselProgress = element.querySelector('.ist-carouselProgress');
    const carouselProgressBar = element.querySelector<HTMLElement>(
      '.ist-carouselProgress__bar'
    );

    // もし、.ist-carouselProgress / .ist-carouselProgress__bar があれば、スライダーの現在地表示
    if (carouselProgress && carouselProgressBar) {
      // 表示領域に完全に収まるスライドの数を計算
      const calculateVisibleSlides = () => {
        // 表示領域（トラック）の取得
        const track = element.querySelector('.splide__track');
        if (!track) {
          console.warn('Splideのトラックが見つかりません');
          return 0;
        }
        const trackWidth = track.clientWidth;

        // 1枚目のスライドを取得（全スライドの幅は同じと仮定）
        const slide = track.querySelector('.splide__slide');
        if (!slide) {
          console.warn('スライド要素が見つかりません');
          return 0;
        }

        // 1枚のスライドの幅と左右のマージンを取得
        const slideStyle = window.getComputedStyle(slide);
        const slideWidth = slide.clientWidth;
        const marginLeft = parseFloat(slideStyle.marginLeft) || 0;
        const marginRight = parseFloat(slideStyle.marginRight) || 0;

        // 1枚分のトータル幅
        const totalSlideWidth = slideWidth + marginLeft + marginRight;

        // 表示領域に完全に収まるスライドの数を計算
        const visibleSlides = Math.floor(trackWidth / totalSlideWidth);

        return visibleSlides;
      };

      // バーの長さを更新する関数
      const progressAction = () => {
        const visibleSlides = calculateVisibleSlides();
        const end = slider.Components.Controller.getEnd() + 1;
        const currentIndex =
          visibleSlides > 1
            ? slider.index + 1 + (visibleSlides - 1)
            : slider.index + 1;
        const rate = Math.min(currentIndex / end, 1);
        carouselProgressBar.style.width = `${100 * rate}%`;
      };

      // Splideの初期化が終わった際、またはスライドが切り替わる際にバーの長さを更新する
      slider.on('mounted move', progressAction);

      // バーの長さを更新
      progressAction();

      // 自動再生機能なしの場合 move イベントをバインド
      const nextButton = element.querySelector('.splide__arrow--next');
      const prevButton = element.querySelector('.splide__arrow--prev');
      if (nextButton && prevButton) {
        // next/prev の独自ハンドラを簡素化（据え置きバグ防止）
        nextButton.addEventListener('click', () => {
          slider.go('>');
          // 移動完了後にフォーカスを移動
          setTimeout(() => {
            this.focusLeftmostSlide(slider);
          }, 100);
        });
        prevButton.addEventListener('click', () => {
          slider.go('<');
          // 移動完了後にフォーカスを移動
          setTimeout(() => {
            this.focusLeftmostSlide(slider);
          }, 100);
        });
      }
    }

    /**
     * 追加機能01: .splide__trackWrapper の幅を調整
     */
    const trackWrapper = element.querySelector(
      '.splide__trackWrapper'
    ) as HTMLElement | null;
    if (trackWrapper) {
      const adjustTrackWrapperWidth = () => {
        // bodyのクライアント幅を取得
        const bodyWidth = document.body.clientWidth;

        // .splide__trackWrapperの左端の位置を取得（ページ全体の左からの距離）
        const trackRect = trackWrapper.getBoundingClientRect();
        const trackLeft = trackRect.left + window.scrollX;

        // bodyの右端までの距離を計算
        const distanceToRight = bodyWidth - trackLeft;

        // 計算した距離を.splide__trackWrapperの幅として設定
        trackWrapper.style.width = `${distanceToRight}px`;
      };

      /**
       * デバウンス関数
       * @param func - 実行する関数
       * @param wait - 待機時間（ミリ秒）
       * @returns デバウンスされた関数
       */
      const debounce = (func: Function, wait: number) => {
        let timeout: number | undefined;
        return () => {
          clearTimeout(timeout);
          timeout = window.setTimeout(() => {
            func();
          }, wait);
        };
      };

      // デバウンスされた関数を作成
      const debouncedAdjustTrackWrapperWidth = debounce(
        adjustTrackWrapperWidth,
        100
      );

      // 初回調整
      adjustTrackWrapperWidth();

      // リサイズ時にデバウンスされた関数を呼び出す
      window.addEventListener('resize', debouncedAdjustTrackWrapperWidth);

      // クリーンアップ用に、スライダーが破棄された際にイベントリスナーを解除
      const onDestroy = () => {
        window.removeEventListener('resize', debouncedAdjustTrackWrapperWidth);
      };
      slider.on('destroy', onDestroy);
    }
  }

  /**
   * スライドの可視性変更時にtabindexとaria-hiddenを管理します
   * @param slider - Splideインスタンス
   */
  private setupTabindexManagement(slider: Splide): void {
    // visibleイベント: スライドが表示される時
    slider.on('visible', () => {
      // is-activeクラスを基準にaria-hiddenを制御
      this.controlAriaHidden(slider);
    });

    // hiddenイベント: スライドが非表示になる時
    slider.on('hidden', () => {
      // is-activeクラスを基準にaria-hiddenを制御
      this.controlAriaHidden(slider);
    });
  }

  /**
   * aria-hiddenを制御してis-activeクラスを持つスライド以外を非表示にする
   * @param slider - Splideインスタンス
   */
  private controlAriaHidden(slider: Splide): void {
    const slides = slider.Components.Elements.slides as HTMLElement[];

    slides.forEach((slide) => {
      const isActive = slide.classList.contains('is-active');

      if (isActive) {
        // is-activeクラスを持つスライドはアクティブ
        slide.setAttribute('aria-hidden', 'false');
        // 子要素のaria-hiddenも解除
        this.setAriaHiddenForChildren(slide, false);
        // フォーカス可能要素を復元
        this.restoreTabindexForSlide(slide);
        // movedイベント後にtabindex="-1"を削除
        this.removeTabindexMinusOne(slide);
      } else {
        // is-activeクラスを持たないスライドは非アクティブ
        slide.setAttribute('aria-hidden', 'true');
        // 子要素にもaria-hiddenを設定
        this.setAriaHiddenForChildren(slide, true);
        // フォーカス可能要素を無効化
        this.setTabindexForSlide(slide, '-1');
      }
    });
  }

  /**
   * スライドの子要素にaria-hiddenを設定/解除する
   * @param slideElement - スライド要素
   * @param hidden - trueの場合はaria-hidden="true"、falseの場合はaria-hiddenを削除
   */
  private setAriaHiddenForChildren(
    slideElement: HTMLElement,
    hidden: boolean
  ): void {
    // スライド内のすべての子要素を取得
    const allChildren = slideElement.querySelectorAll('*');
    allChildren.forEach((child) => {
      if (hidden) {
        // 元のaria-hiddenの値を保存（まだ保存されていない場合のみ）
        if (!child.hasAttribute('data-original-aria-hidden')) {
          const originalAriaHidden = child.getAttribute('aria-hidden');
          child.setAttribute(
            'data-original-aria-hidden',
            originalAriaHidden || ''
          );
        }
        child.setAttribute('aria-hidden', 'true');
      } else {
        // aria-hiddenを削除（ただし、元々aria-hiddenが設定されていた要素は保持）
        const originalAriaHidden = child.getAttribute(
          'data-original-aria-hidden'
        );
        if (originalAriaHidden === null || originalAriaHidden === '') {
          // 元々aria-hiddenがなかった場合は削除
          child.removeAttribute('aria-hidden');
        } else {
          // 元々aria-hiddenがあった場合は復元
          child.setAttribute('aria-hidden', originalAriaHidden);
        }
        // 保存した値を削除
        child.removeAttribute('data-original-aria-hidden');
      }
    });
  }

  /**
   * .splide__track配下でのみ矢印キー操作を有効にする
   * @param slider - Splideインスタンス
   * @param element - スライダー要素
   */
  private setupTrackKeyboardNavigation(slider: Splide, element: Element): void {
    const track = element.querySelector('.splide__track');
    if (!track) return;

    // .splide__trackにキーボードイベントリスナーを追加
    track.addEventListener('keydown', (event: Event) => {
      const keyboardEvent = event as KeyboardEvent;

      // 矢印キーのみを処理
      if (
        keyboardEvent.key === 'ArrowLeft' ||
        keyboardEvent.key === 'ArrowRight'
      ) {
        event.preventDefault();

        if (keyboardEvent.key === 'ArrowLeft') {
          slider.go('<');
        } else {
          slider.go('>');
        }

        // 矢印キー操作後はフォーカスを移動
        setTimeout(() => {
          this.focusLeftmostSlide(slider);
        }, 50);
      }
    });
  }

  /**
   * スライド内からフォーカス可能要素を取得
   * @param el - スライド要素
   * @returns フォーカス可能要素の配列
   */
  private getFocusableElements(el: HTMLElement): HTMLElement[] {
    return Array.from(
      el.querySelectorAll<HTMLElement>(
        'a,button,input,select,textarea,iframe,[tabindex]:not([tabindex="-1"]),[role="button"]'
      )
    ).filter(
      (n) =>
        !n.hasAttribute('disabled') && n.getAttribute('aria-hidden') !== 'true'
    );
  }

  /**
   * is-activeクラスを持つスライド内の先頭フォーカス要素にフォーカスを送る
   * @param slider - Splideインスタンス
   */
  private focusLeftmostSlide(slider: Splide): void {
    const slides = slider.Components.Elements.slides as HTMLElement[];
    // is-activeクラスを持つスライドを探す
    const activeSlide = slides.find((slide) =>
      slide.classList.contains('is-active')
    );

    if (!activeSlide) {
      // is-activeクラスが見つからない場合は、少し待って再試行
      setTimeout(() => {
        const retrySlides = slider.Components.Elements.slides as HTMLElement[];
        const retryActiveSlide = retrySlides.find((slide) =>
          slide.classList.contains('is-active')
        );
        if (retryActiveSlide) {
          const target =
            retryActiveSlide.querySelector<HTMLElement>('[data-focus]') ||
            this.getFocusableElements(retryActiveSlide)[0];
          target?.focus({ preventScroll: true });
        }
      }, 50);
      return;
    }

    // 推奨: 明示ターゲット（例：カード全体のリンク）に data-focus を付ける
    const target =
      activeSlide.querySelector<HTMLElement>('[data-focus]') ||
      this.getFocusableElements(activeSlide)[0];

    // ターゲットが無ければ何もしない（自然フォーカス維持）
    if (target) {
      // フォーカス可能な要素が見つかった場合のみフォーカスを移動
      target.focus({ preventScroll: true });
    }
  }

  /**
   * スライドの子要素のtabindexをリセット
   * @param slideElement - スライド要素
   */
  private restoreTabindexForSlide(slideElement: HTMLElement): void {
    // フォーカス可能な要素をすべて取得
    const focusableElements = slideElement.querySelectorAll<HTMLElement>(
      'a,button,input,select,textarea,iframe,[tabindex],[role="button"]'
    );

    focusableElements.forEach((child) => {
      const originalTabindex = child.getAttribute('data-original-tabindex');
      if (originalTabindex !== null) {
        if (originalTabindex === '') {
          // 元々tabindexがなかった場合は削除
          child.removeAttribute('tabindex');
        } else {
          // 元のtabindexを復元
          child.setAttribute('tabindex', originalTabindex);
        }
        child.removeAttribute('data-original-tabindex');
      }
    });
  }

  /**
   * スライドの子要素からtabindex="-1"を削除
   * movedイベント後にアクティブスライドのtabindex="-1"を削除する
   * @param slideElement - スライド要素
   */
  private removeTabindexMinusOne(slideElement: HTMLElement): void {
    // フォーカス可能な要素をすべて取得
    const focusableElements = slideElement.querySelectorAll<HTMLElement>(
      'a,button,input,select,textarea,iframe,[tabindex],[role="button"]'
    );

    focusableElements.forEach((child) => {
      // tabindex="-1"の場合は削除
      if (child.getAttribute('tabindex') === '-1') {
        // data-original-tabindexが保存されている場合は復元
        const originalTabindex = child.getAttribute('data-original-tabindex');
        if (originalTabindex !== null) {
          if (originalTabindex === '') {
            // 元々tabindexがなかった場合は削除
            child.removeAttribute('tabindex');
          } else {
            // 元のtabindexを復元
            child.setAttribute('tabindex', originalTabindex);
          }
        } else {
          // data-original-tabindexがない場合は単純に削除
          // （デフォルトフォーカス可能要素は自然にフォーカス可能になる）
          child.removeAttribute('tabindex');
        }
      }
    });
  }

  /**
   * スライドの子要素のtabindexを設定
   * @param slideElement - スライド要素
   * @param tabindex - tabindexの値
   */
  private setTabindexForSlide(
    slideElement: HTMLElement,
    tabindex: string
  ): void {
    // フォーカス可能な要素をすべて取得
    const focusableElements = slideElement.querySelectorAll<HTMLElement>(
      'a,button,input,select,textarea,iframe,[tabindex],[role="button"]'
    );

    focusableElements.forEach((child) => {
      if (!child.hasAttribute('data-original-tabindex')) {
        // 元のtabindexを保存（デフォルトフォーカス可能要素はnull）
        const originalTabindex = child.getAttribute('tabindex');
        child.setAttribute('data-original-tabindex', originalTabindex || '');
      }
      child.setAttribute('tabindex', tabindex);
    });
  }

  /**
   * ウィンドウのリサイズ時にSplideを初期化または破棄します
   * @param element - スライダー要素
   * @param id - 要素のID
   */
  private handleResize(element: Element, id: string): void {
    const slider = this.splideInstances.get(element);
    const shouldInitialize = window.innerWidth >= this.breakpoint;
    const hasSlider = !!slider;

    if (shouldInitialize && !hasSlider) {
      // 初期化
      this.initializeSplide(element, id);
    } else if (!shouldInitialize && hasSlider) {
      // 破棄
      slider?.destroy();
      this.splideInstances.delete(element);
    }
  }

  /**
   * スライド情報を更新
   */
  private updateSlideInfo(
    slider: Splide,
    currentElement: Element,
    lengthElement: Element
  ): void {
    // 現在のインデックスを計算（ループ考慮）
    const currentIndex = (slider.index % slider.length) + 1;
    currentElement.textContent = String(currentIndex);

    // 実際のスライド数を取得（ループによる重複を除外）
    lengthElement.textContent = String(slider.length);
  }
}

export default Slider;
